import { motion } from "framer-motion";
import type { MenuCategory } from "../../types/menu";
import MenuCard from "./MenuCard";
import MenuCardFeatured from "./MenuCardFeatured";

interface MenuSectionProps {
  category: MenuCategory;
}

export default function MenuSection({ category }: MenuSectionProps) {
  const featuredItems = category.items.filter((item) => item.featured);
  const regularItems = category.items.filter((item) => !item.featured);

  return (
    <section id={category.id} className="scroll-mt-32">
      {/* Section header */}
      <motion.div
        className="flex items-center gap-4 mb-10"
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="flex flex-col gap-1.5">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-[var(--color-text-base)]">
            {category.name}
          </h2>
          {category.description && (
            <p className="text-[var(--color-text-muted)] text-sm sm:text-base">
              {category.description}
            </p>
          )}
        </div>
        {/* Decorative line */}
        <motion.div
          className="hidden md:block flex-1 h-px bg-gradient-to-r from-primary/20 to-transparent ml-4"
          initial={{ scaleX: 0, originX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        />
      </motion.div>

      {/* Featured items */}
      {featuredItems.length > 0 && (
        <div className="space-y-6 mb-8">
          {featuredItems.map((item) => (
            <MenuCardFeatured key={item.id} item={item} />
          ))}
        </div>
      )}

      {/* Regular items */}
      {regularItems.length > 0 && (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-0">
          {regularItems.map((item, i) => (
            <div key={item.id} className="break-inside-avoid mb-6">
              <MenuCard item={item} index={i} />
            </div>
          ))}
        </div>
      )}

      {/* Add-ons */}
      {category.addons && category.addons.length > 0 && (
        <motion.div
          className="mt-8 rounded-2xl border border-primary/10 bg-sand/60 p-5 sm:p-6"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <p className="font-heading font-bold text-sm uppercase tracking-widest text-primary mb-4">
            {category.addonsLabel ?? "Aderezos"}
          </p>
          <div className="flex flex-wrap gap-3">
            {category.addons.map((addon, i) => (
              <motion.div
                key={addon.name}
                className="flex items-center gap-2 bg-white rounded-xl px-4 py-2 shadow-sm border border-black/[0.04]"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{
                  delay: i * 0.03,
                  duration: 0.25,
                  ease: "easeOut",
                }}
              >
                <span className="text-sm font-semibold text-[var(--color-text-base)]">
                  {addon.name}
                </span>
                <span className="text-sm font-extrabold text-primary">
                  +${addon.price.toFixed(2)}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </section>
  );
}
